import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";
import {
  getTaskByIdAndTypeQueryConfig,
  TaskByIdAndTypeResponse,
  useGetTaskByIdAndType,
} from "@coworker/apprestructured/src/tasks/hooks/useGetTaskByIdAndType";
import { useQueries } from "@tanstack/react-query";
import { TaskData } from "@coworker/apprestructured/src/testbuy/types/testbuyTaskData";
import { getTaskQueryConfig } from "@coworker/apprestructured/src/task-management/services/useTask";
import { useTask } from "@coworker/apprestructured/src/task-management/services/useTask";
import { Task } from "@coworker/apprestructured/src/task-management/types/Task";
import { TaskState } from "@coworker/types/lib/tasks/constants";

// V1 tasks already carry an uppercase state (e.g. "COMPLETED") and a
// `finished_at` ISO timestamp.
const buildProductFollowUpTaskV1 = (
  data: TaskByIdAndTypeResponse,
  productId: string,
  taskId: string
): TaskData => ({
  articleId: productId,
  taskId,
  state: data.state as TaskState,
  finishedAt: data.finished_at
    ? new Date(data.finished_at).getTime()
    : undefined,
});

// V2 tasks use three states ("pending" | "completed" | "closed") while V1's
// TaskData.state has four ("UNASSIGNED" | "ASSIGNED" | "COMPLETED" | "CLOSED").
// "pending" maps to ASSIGNED/UNASSIGNED depending on whether the task has an
// assignee; completion timestamp comes from `completed_at`/`closed_at`
// instead of the single `finished_at` field V1 tasks use.
const mapV2State = (data: Task): TaskData["state"] => {
  if (data.state === "completed") return "COMPLETED";
  if (data.state === "closed") return "CLOSED";
  return data.assigned_user_id ? "ASSIGNED" : "UNASSIGNED";
};

const buildProductFollowUpTaskV2 = (
  data: Task,
  productId: string,
  taskId: string
): TaskData => ({
  articleId: productId,
  taskId,
  state: mapV2State(data),
  finishedAt: data.completed_at
    ? new Date(data.completed_at).getTime()
    : data.closed_at
      ? new Date(data.closed_at).getTime()
      : undefined,
});

// V2 task IDs are numeric bigint strings, V1 task IDs are UUIDs.
const isNumericId = (id: string) => /^\d+$/.test(id);

export const useGetTestbuyFollowUpTask = (taskId: string) => {
  const isV2 = isNumericId(taskId);

  const { data: v1Data, isLoading: isV1Loading } = useGetTaskByIdAndType(
    isV2 ? "" : taskId,
    TaskType.TESTBUY_FOLLOW_UP
  );
  const { data: v2Data, isLoading: isV2Loading } = useTask(
    isV2 ? taskId : null
  );

  const mappedTask = isV2
    ? v2Data &&
      buildProductFollowUpTaskV2(
        v2Data,
        (v2Data.details?.["article_id"] as string) ?? "",
        v2Data.id
      )
    : v1Data &&
      buildProductFollowUpTaskV1(v1Data, v1Data.product_article_id, v1Data.id);

  return {
    isLoading: isV2 ? isV2Loading : isV1Loading,
    data: mappedTask,
    isV2Task: isV2,
  };
};

export const useGetTestbuyFollowUpTasks = (taskIds: string[]) => {
  const isV2TaskAtIndex = (index: number) => {
    const taskId = taskIds[index];

    return !!taskId && isNumericId(taskId);
  };

  const results = useQueries({
    queries: taskIds.map((taskId) =>
      isNumericId(taskId)
        ? getTaskQueryConfig(taskId)
        : getTaskByIdAndTypeQueryConfig(taskId, TaskType.TESTBUY_FOLLOW_UP)
    ),
  });

  return {
    isLoading: results.some((result) => result.isLoading),
    data: results
      .map((result, index) => {
        const data = result.data;

        if (!data) {
          return undefined;
        }

        if (isV2TaskAtIndex(index)) {
          const v2Data = data as Task;

          return buildProductFollowUpTaskV2(
            v2Data,
            (v2Data.details?.["article_id"] as string) ?? "",
            v2Data.id
          );
        }

        const v1Data = data as TaskByIdAndTypeResponse;

        return buildProductFollowUpTaskV1(
          v1Data,
          v1Data.product_article_id,
          v1Data.id
        );
      })
      .filter((task): task is TaskData => !!task),
    isV2Task: taskIds.some((taskId) => !!taskId && isNumericId(taskId)),
  };
};
